import React from 'react';
import {
  DatePickerField,
  SimpleFormSingleDropdown,
} from '../../shared/form/Fields';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  BirchDatePickerFormat,
  SendEmailAtOptionsResponse,
} from '../../shared/types';
import moment from 'moment-timezone';
import { withLocalePrefix } from '../../../shared/utils/translation';

const t = withLocalePrefix('candidate_profile_redesign.modals');

type SendLaterRowProps = Pick<
  SendEmailAtOptionsResponse,
  'whenEmailOptions' | 'customTimeOptions'
> & {
  shouldValidate?: boolean;
};

const SendLaterRow = ({
  whenEmailOptions,
  customTimeOptions,
  shouldValidate = true,
  dateFormatBirchPicker,
  timezone,
}: SendLaterRowProps & {
  dateFormatBirchPicker: BirchDatePickerFormat;
  timezone: string;
}) => {
  const { watch, getValues } = useFormContext();

  const when = watch('when');

  const showOptions = when ? when.value === 'custom' : false;

  const validate = (mode: 'date' | 'time') => {
    if (when && when.value !== 'custom') {
      return true;
    }
    if (!shouldValidate) {
      return true;
    }
    const sendDate = getValues('sendDate');
    const time = getValues('time');

    if (!time || !sendDate) {
      return false;
    }

    const formattedDate = sendDate.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    let customDateTime;
    let currentDateTime;

    if (timezone) {
      currentDateTime = moment.tz(timezone);
      customDateTime = moment.tz(
        `${formattedDate} ${time.label}`,
        'MM/DD/YYYY h:mma',
        timezone
      );
    } else {
      currentDateTime = moment();
      customDateTime = moment(
        `${formattedDate} ${time.label}`,
        'MM/DD/YYYY h:mma'
      );
    }

    if (mode === 'date' && customDateTime?.isBefore(currentDateTime, 'day')) {
      return t('email.schedule.send_date_validation');
    } else if (
      mode === 'time' &&
      customDateTime?.isSame(currentDateTime, 'day') &&
      customDateTime.isBefore(currentDateTime)
    ) {
      return t('email.schedule.send_time_validation');
    }
    return true;
  };

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: '1', maxWidth: '50%' }}>
          <SimpleFormSingleDropdown
            items={whenEmailOptions}
            name="when"
            label={t('email.schedule.when')}
            defaultValue={whenEmailOptions[0]}
            clearable={false}
          />
        </div>

        {showOptions && (
          <div
            style={{
              flex: '0 0 20%',
              paddingLeft: '16px',
              boxSizing: 'border-box',
            }}
          >
            <DatePickerField
              id="sendDate"
              required={showOptions && shouldValidate}
              name="sendDate"
              label={t('email.schedule.send_date')}
              dateFormat={dateFormatBirchPicker}
              minDate={moment().toDate()}
              rules={{
                required:
                  showOptions && shouldValidate
                    ? t('email.schedule.send_date_required')
                    : false,
                validate: () => {
                  return validate('date');
                },
              }}
            />
          </div>
        )}
        {showOptions && (
          <div
            style={{
              flex: '0 0 30%',
              paddingLeft: '16px',
              boxSizing: 'border-box',
            }}
          >
            <SimpleFormSingleDropdown
              items={customTimeOptions}
              name="time"
              label={t('email.schedule.time')}
              clearable={false}
              rules={{
                required:
                  showOptions && shouldValidate
                    ? t('email.schedule.time_required')
                    : false,
                validate: () => {
                  return validate('time');
                },
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SendLaterRow;
